import React, { useState, useEffect } from 'react';
import { useAuth, useFirebase } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext'; // Import the subscription hook
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { updateProfile, sendPasswordResetEmail, EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions'; // Import for cloud functions
import { NavLink } from 'react-router-dom'; // Import NavLink for the upgrade button
import { Save, Loader, Send, PlusCircle, XCircle, Edit, CreditCard } from 'lucide-react';

import * as templates from '../data/cpdTemplates';
import { cpdCategories } from '../data/cpdData';

const allTemplates = Object.values(templates);

// --- Re-authentication Modal Component (Unchanged) ---
const ReauthModal = ({ show, onClose, onSubmit, setPassword, error }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
                <h3 className="text-lg font-bold mb-4">Confirm Your Identity</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">For your security, please enter your current password to continue.</p>
                <form onSubmit={onSubmit}>
                    <label htmlFor="password-reauth" className="block text-sm font-medium">Password</label>
                    <input id="password-reauth" type="password" onChange={(e) => setPassword(e.target.value)} className="w-full mt-1 p-2 bg-gray-100 dark:bg-gray-900 rounded-md border" required />
                    {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                    <div className="flex justify-end space-x-2 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 rounded-md">Cancel</button>
                        <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md">Confirm</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const UserProfilePage = () => {
    const { user, auth } = useAuth();
    const { db } = useFirebase();
    const { subscription } = useSubscription(); // Use the subscription hook
    
    // States for various parts of the page
    const [displayName, setDisplayName] = useState('');
    const [cpdRequirements, setCpdRequirements] = useState(templates.defaultCpdRequirement);
    const [status, setStatus] = useState('idle');
    const [notification, setNotification] = useState('');

    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [showReauthModal, setShowReauthModal] = useState(false);
    const [passwordForReauth, setPasswordForReauth] = useState('');
    const [reauthError, setReauthError] = useState('');

    useEffect(() => {
        if (user) {
            setDisplayName(user.displayName || '');
            setNewEmail(user.email || '');
        }
        if (!user || !db) return;
        
        const settingsRef = doc(db, 'users', user.uid, 'settings', 'cpdRequirements');
        const fetchSettings = async () => {
            setStatus('loading');
            try {
                const docSnap = await getDoc(settingsRef);
                setCpdRequirements(docSnap.exists() && docSnap.data().name ? docSnap.data() : templates.defaultCpdRequirement);
            } catch (error) {
                setNotification('Could not load your settings.');
            } finally {
                setStatus('idle');
            }
        };
        fetchSettings();
    }, [user, db]);

    // --- New Subscription Management Handler ---
    const manageSubscription = async () => {
        const functions = getFunctions();
        const createPortalLink = httpsCallable(functions, 'createCustomerPortalLink');
        try {
            const response = await createPortalLink();
            const { url } = response.data;
            window.location.assign(url); // Redirect to Stripe Customer Portal
        } catch (error) {
            console.error("Error creating portal link:", error);
            setNotification("Could not manage subscription. Please try again.");
        }
    };

    // --- Email Update Handlers (Unchanged) ---
    const handleEmailEditStart = () => setIsEditingEmail(true);
    const handleEmailEditCancel = () => { setIsEditingEmail(false); setNewEmail(user.email); };
    const handleEmailUpdate = (e) => { e.preventDefault(); if (newEmail === user.email) { setIsEditingEmail(false); return; } setShowReauthModal(true); setReauthError(''); };
    const handleReauthSubmit = async (e) => {
        e.preventDefault();
        if (!passwordForReauth) { setReauthError('Password is required.'); return; }
        try {
            const credential = EmailAuthProvider.credential(user.email, passwordForReauth);
            await reauthenticateWithCredential(user, credential);
            await verifyBeforeUpdateEmail(user, newEmail);
            setShowReauthModal(false); setPasswordForReauth(''); setIsEditingEmail(false);
            setNotification(`Verification email sent to ${newEmail}. Please confirm to finalize the change.`);
        } catch (error) {
            setReauthError('Incorrect password. Please try again.');
        }
    };

    // Other handlers remain the same...
    const handleApplyTemplate = (template) => setCpdRequirements(template);
    const handleSetCustom = () => setCpdRequirements({ name: "Custom Requirement", cycleYears: 1, totalHours: 50, categoryGroups: [] });
    const handleRequirementChange = (field, value) => setCpdRequirements(prev => ({ ...prev, name: "Custom Requirement", [field]: Number(value) }));
    const handleAddGroup = () => { const newGroup = { groupName: 'New Category Group', requiredHours: 10, mapsFrom: [] }; setCpdRequirements(prev => ({ ...prev, name: "Custom Requirement", categoryGroups: [...(prev.categoryGroups || []), newGroup] })); };
    const handleRemoveGroup = (index) => setCpdRequirements(prev => ({ ...prev, name: "Custom Requirement", categoryGroups: prev.categoryGroups.filter((_, i) => i !== index) }));
    const handleGroupChange = (index, field, value) => { const updatedGroups = [...cpdRequirements.categoryGroups]; updatedGroups[index] = { ...updatedGroups[index], [field]: value }; setCpdRequirements(prev => ({ ...prev, name: "Custom Requirement", categoryGroups: updatedGroups })); };
    const handleCategoryInGroupToggle = (groupIndex, categoryName) => { const updatedGroups = [...cpdRequirements.categoryGroups]; const group = updatedGroups[groupIndex]; const mappedCategories = group.mapsFrom || []; if (mappedCategories.includes(categoryName)) { group.mapsFrom = mappedCategories.filter(cat => cat !== categoryName); } else { group.mapsFrom = [...mappedCategories, categoryName]; } setCpdRequirements(prev => ({ ...prev, name: "Custom Requirement", categoryGroups: updatedGroups })); };
    const handleSaveChanges = async () => { if (!user || !db) return; setStatus('saving'); setNotification(''); try { const settingsRef = doc(db, 'users', user.uid, 'settings', 'cpdRequirements'); await setDoc(settingsRef, cpdRequirements, { merge: true }); if (displayName !== user.displayName) { await updateProfile(auth.currentUser, { displayName }); } setStatus('success'); setNotification('Profile and settings saved successfully!'); } catch (error) { setStatus('error'); setNotification(`Error: ${error.message}`); } setTimeout(() => { setStatus('idle'); setNotification(''); }, 3000); };
    const handlePasswordReset = async () => { if (!user?.email) return; try { await sendPasswordResetEmail(auth, user.email); setNotification('Password reset email sent! Please check your inbox.'); } catch (error) { setNotification(`Error: ${error.message}`); } };
    
    if (status === 'loading') return <div className="text-center p-8"><Loader className="animate-spin inline-block" /></div>;

    return (
        <div>
            <ReauthModal show={showReauthModal} onClose={() => setShowReauthModal(false)} onSubmit={handleReauthSubmit} setPassword={setPasswordForReauth} error={reauthError} />

            <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2">My Profile</h1>
                <p className="text-gray-600 dark:text-gray-400">Update your details and manage your CPD requirements.</p>
            </div>

            {/* Account Details Section */}
            <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-300 dark:border-gray-700 mb-8">
                <h2 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4">Account Details</h2>
                <div className="space-y-4">
                    {/* Display Name */}
                    <div>
                        <label htmlFor="displayName" className="block text-md font-medium">Display Name</label>
                        <input type="text" id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="w-full mt-1 p-2 bg-white dark:bg-gray-900 rounded-md border" />
                    </div>
                     {/* Email */}
                     <div>
                        <label className="block text-md font-medium">Email</label>
                        <div className="flex items-center gap-4 mt-1">
                            <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} readOnly={!isEditingEmail} className={`w-full p-2 bg-white dark:bg-gray-900 rounded-md border ${isEditingEmail ? '' : 'bg-gray-100 dark:bg-gray-800'}`} />
                            {!isEditingEmail ? (
                                <button onClick={handleEmailEditStart} className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-semibold shadow-md hover:bg-gray-700">
                                    <Edit size={16} className="mr-2"/> Edit
                                </button>
                            ) : (
                                <div className="flex gap-2">
                                     <button onClick={handleEmailUpdate} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold">Save</button>
                                     <button onClick={handleEmailEditCancel} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-sm">Cancel</button>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Password */}
                    <div>
                        <label className="block text-md font-medium">Password</label>
                         <button onClick={handlePasswordReset} className="mt-1 flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-semibold shadow-md hover:bg-gray-700">
                           <Send size={16} className="mr-2"/> Send Password Reset Email
                        </button>
                    </div>

                    {/* --- NEW Subscription Section --- */}
                    <div>
                        <label className="block text-md font-medium">Subscription</label>
                        <div className="mt-1 flex items-center gap-4">
                             {subscription ? (
                                <div className="flex-grow">
                                    <p className="p-2 bg-white dark:bg-gray-900 rounded-md border">
                                        You are on the <strong className={subscription.isActive ? "text-green-500" : "text-yellow-500"}>
                                            {subscription.isActive ? 'Pro Tier' : 'Free Tier'}
                                        </strong>.
                                    </p>
                                </div>
                            ) : (
                                <p className="p-2 bg-white dark:bg-gray-900 rounded-md border">Loading status...</p>
                            )}
                            
                            {subscription && (
                                <>
                                    {subscription.isActive ? (
                                        <button onClick={manageSubscription} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold shadow-md hover:bg-blue-700">
                                            <CreditCard size={16} className="mr-2"/> Manage
                                        </button>
                                    ) : (
                                        <NavLink to="/pricing" className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold shadow-md hover:bg-green-700">
                                            Upgrade to Pro
                                        </NavLink>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* CPD Requirements Section (Unchanged) */}
            <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-lg border">
                 <h2 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4">CPD Requirements</h2>
                 {/* ... all the detailed CPD requirements JSX remains the same ... */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Apply a Template</label>
                    <div className="flex flex-wrap gap-2">
                        {allTemplates.map(t => (<button key={t.name} onClick={() => handleApplyTemplate(t)} className={`px-3 py-2 text-sm rounded-md transition-colors ${cpdRequirements.name === t.name ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300'}`}>{t.name}</button>))}
                        <button onClick={handleSetCustom} className={`px-3 py-2 text-sm rounded-md transition-colors ${cpdRequirements.name === 'Custom Requirement' ? 'bg-blue-600 text-white' : 'bg-gray-500 text-white hover:bg-gray-600'}`}>Custom</button>
                    </div>
                </div>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="cycleYears" className="block text-md font-medium">CPD Cycle Length (Years)</label>
                            <input type="number" id="cycleYears" value={cpdRequirements.cycleYears || 1} onChange={(e) => handleRequirementChange('cycleYears', e.target.value)} className="w-full mt-1 p-2 bg-white dark:bg-gray-900 rounded-md border" />
                        </div>
                        <div>
                            <label htmlFor="totalHours" className="block text-md font-medium">Total Hours Required per Cycle</label>
                            <input type="number" id="totalHours" value={cpdRequirements.totalHours || 0} onChange={(e) => handleRequirementChange('totalHours', e.target.value)} className="w-full mt-1 p-2 bg-white dark:bg-gray-900 rounded-md border"/>
                        </div>
                    </div>
                    <div>
                        <h4 className="text-md font-medium">Category Groups</h4>
                        <div className="mt-2 space-y-4">
                            {(cpdRequirements.categoryGroups || []).map((group, index) => (
                                <div key={index} className="p-4 border rounded-lg bg-white dark:bg-gray-900">
                                    <div className="flex justify-between items-center mb-4">
                                        <input type="text" value={group.groupName} onChange={(e) => handleGroupChange(index, 'groupName', e.target.value)} className="text-lg font-semibold bg-transparent border-b w-1/2" />
                                        <button onClick={() => handleRemoveGroup(index)}><XCircle className="text-red-500"/></button>
                                    </div>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium">Required Hours for this Group</label>
                                        <input type="number" value={group.requiredHours} onChange={(e) => handleGroupChange(index, 'requiredHours', Number(e.target.value))} className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded-md border w-1/3" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Included Activity Types</label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                            {cpdCategories.map(cat => (<div key={cat.name} className="flex items-center"><input type="checkbox" id={`${index}-${cat.name}`} checked={(group.mapsFrom || []).includes(cat.name)} onChange={() => handleCategoryInGroupToggle(index, cat.name)} className="h-4 w-4 rounded" /><label htmlFor={`${index}-${cat.name}`} className="ml-2">{cat.name}</label></div>))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button onClick={handleAddGroup} className="flex items-center px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300"><PlusCircle size={16} className="mr-2"/> Add Category Group</button>
                        </div>
                    </div>
                </div>
                 <div className="mt-8 flex flex-col items-end">
                    <button onClick={handleSaveChanges} disabled={status === 'saving'} className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg font-semibold shadow-md hover:bg-green-700 disabled:bg-gray-400">
                        {status === 'saving' ? <Loader size={18} className="animate-spin mr-2"/> : <Save size={18} className="mr-2"/>}
                        {status === 'saving' ? 'Saving...' : 'Save All Changes'}
                    </button>
                    {notification && <p className={`text-sm text-right mt-2 ${status === 'error' ? 'text-red-500' : 'text-green-500'}`}>{notification}</p>}
                </div>
            </div>
        </div>
    );
};

export default UserProfilePage;